//import { parse } from 'tinyduration'
import { number, sum, round, smaller, multiply } from "mathjs";
import Store from "@/vuex";
import Router from "@/router/router";
import Vue from "vue";
import VueWebStorage from "vue-web-storage";

Vue.use(VueWebStorage, {
  prefix: "", // default `app_`
  drivers: ["session", "local"], // default 'local'
});

export function log(...value) {
  if (process.env.VUE_APP_MODE !== "development") return;
  window.console.log(...value);
}

// ============================================================================
// RYBBIT ANALYTICS
// ============================================================================

/**
 * Configurazione Rybbit
 */
const RYBBIT_CONFIG = {
  // Mappa eventi GA4 -> nomi Rybbit normalizzati
  eventMap: {
    'confirmedCheckout': 'checkout_completed',
    'failedCheckout': 'checkout_failed',
    'initiateCheckout': 'checkout_started',
    'begin_checkout': 'checkout_started',
    'add_to_cart': 'cart_add',
    'purchase': 'checkout_completed',
    'book': 'booking_completed',
  },
  // Eventi da ignorare (rumorosi o duplicati)
  skipEvents: [
    'confirmedCheckout',  // Duplicato: purchase ha dati migliori
    'welfare',            // Rumoroso: triggerato ad ogni sessione
    'customizationUse',   // Rumoroso: triggerato ad ogni sessione
  ],
  // Eventi con lista items (usano prezzo minimo)
  itemListEvents: ['view_item', 'view_item_list'],
};

/**
 * Traccia evento Rybbit (chiamata diretta)
 * @param {string} eventName - Nome evento (snake_case consigliato)
 * @param {object} props - Proprietà evento (opzionale)
 */
export function rybbit_event(eventName, props = {}) {
  if (!window.rybbit) return;
  if (!eventName || typeof eventName !== 'string') return;
  window.rybbit.event(eventName, props);
}

// ----------------------------------------------------------------------------
// Funzioni private per rybbit_event_from_custom
// ----------------------------------------------------------------------------

/**
 * Valida i parametri in ingresso
 * @returns {boolean} true se validi
 */
function _rybbit_validateAction(action) {
  if (!action) return false;
  if (typeof action === 'object') {
    return action.event && typeof action.event === 'string';
  }
  return true;
}

/**
 * Estrae nome evento e props da un oggetto GA4 ecommerce
 * @returns {{ eventName: string, props: object }}
 */
function _rybbit_parseGA4Event(action) {
  const eventName = action.event;
  const props = {};

  if (!action.ecommerce) {
    return { eventName, props };
  }

  const ecommerce = action.ecommerce;

  // Eventi con lista items: usa prezzo minimo
  if (RYBBIT_CONFIG.itemListEvents.includes(eventName) && ecommerce.items && ecommerce.items.length > 0) {
    const prices = ecommerce.items.map(item => parseFloat(item.price) || 0);
    props.value = Math.min(...prices);
    props.items_count = ecommerce.items.length;
  } else {
    // Altri eventi: gestisci prezzi netto/lordo
    const priceNet = parseFloat(ecommerce.value) || 0;
    const priceGross = parseFloat(ecommerce.live_price) || 0;
    props.price_gross = priceGross || priceNet;
    props.price_net = priceNet;
    if (priceGross && priceNet > 0) {
      props.markup = (priceGross - priceNet).toFixed(2);
    }
  }

  if (ecommerce.transaction_id) props.transaction_id = ecommerce.transaction_id;
  if (ecommerce.currency) props.currency = ecommerce.currency;

  return { eventName, props };
}

/**
 * Estrae nome evento e props da parametri standard
 * @returns {{ eventName: string, props: object }}
 */
function _rybbit_parseStandardEvent(action, label, value, category) {
  const props = {};
  if (label) props.label = label;
  if (value) props.value = parseFloat(value) || 0;
  if (category) props.category = category;
  return { eventName: action, props };
}

/**
 * Normalizza il nome evento usando la mappa configurata
 */
function _rybbit_mapEventName(eventName) {
  return RYBBIT_CONFIG.eventMap[eventName] || eventName;
}

/**
 * Gestisce l'evento SSO trasformandolo in session_start
 * @returns {{ eventName: string, props: object } | null} null se deve essere skippato
 */
function _rybbit_handleSsoEvent(category, currentProps) {
  // SSO in GA4 viene sparato 3 volte con category diverse
  // Per Rybbit ne mandiamo solo uno con category 'userBehaviour'
  if (category !== 'userBehaviour') {
    return null;
  }

  // Richiede pattern completo: token + utm_source + utm_campaign
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');
  const utmSource = urlParams.get('utm_source');
  const utmCampaign = urlParams.get('utm_campaign');

  if (!token || !utmSource || !utmCampaign) {
    return null;
  }

  // Costruisci props per session_start
  const props = {
    credit_available: currentProps.value || 0,
    utm_source: utmSource,
    utm_campaign: utmCampaign,
  };

  // Aggiungi UTM opzionali
  const utmMedium = urlParams.get('utm_medium');
  const utmContent = urlParams.get('utm_content');
  const utmTerm = urlParams.get('utm_term');
  if (utmMedium) props.utm_medium = utmMedium;
  if (utmContent) props.utm_content = utmContent;
  if (utmTerm) props.utm_term = utmTerm;

  return { eventName: 'session_start', props };
}

/**
 * Normalizza e traccia evento Rybbit da custom_event
 * Gestisce la conversione da formato GA4 a formato Rybbit
 * @param {string|object} action - Nome evento o oggetto GA4
 * @param {string} label - Label evento (opzionale)
 * @param {number} value - Valore evento (opzionale)
 * @param {string} category - Categoria evento (opzionale)
 */
function rybbit_event_from_custom(action, label, value, category) {
  if (!window.rybbit) return;
  if (!_rybbit_validateAction(action)) return;

  // 1. Parsing: estrai eventName e props in base al tipo di action
  let parsed;
  if (typeof action === 'object') {
    parsed = _rybbit_parseGA4Event(action);
  } else {
    parsed = _rybbit_parseStandardEvent(action, label, value, category);
  }

  // 2. Mapping: normalizza il nome evento
  let eventName = _rybbit_mapEventName(parsed.eventName);
  let props = parsed.props;

  // 3. Gestione SSO: trasforma in session_start se applicabile
  if (eventName === 'sso') {
    const ssoResult = _rybbit_handleSsoEvent(category, props);
    if (!ssoResult) return; // Skip
    eventName = ssoResult.eventName;
    props = ssoResult.props;
  }

  // 4. Skip eventi configurati
  if (RYBBIT_CONFIG.skipEvents.includes(eventName)) {
    return;
  }

  // 5. Invia evento
  rybbit_event(eventName, props);
}

export function is_development() {
  if (process.env.VUE_APP_MODE == "development") return true;
  if (process.env.VUE_APP_MODE == "staging") return true;
  return false;
}

export function is_authenticated() {
  let token = Store.state.token;

  // se c'è un token in get ha la precedenza (quello nello state potrebbe essere scaduto)
  if (Router.history.current.query.token)
    token = Router.history.current.query.token;

  // non ho trovato alcun token
  if (!token) return false;

  // ho un token ma NON un'integrazione

  // implementazione cancellara -> integration nello state
  // if(!Store.state.integration || !Store.state.integration.integration) return false

  // implementazione livetravel -> integration solo nel config
  if (!get_config("integration.integration")) return false;

  return true;
}

export function get_config(path = "", fallback = false) {
  let cur = { ...Vue.prototype.$config };

  if (!cur) return fallback;
  if (!path) return cur;

  for (const key of path.split(".")) {
    if (cur == null || typeof cur !== "object") return fallback;
    cur = cur[key];
  }

  return cur == null ? fallback : cur;
}

export function get_availability(price) {
  price = price || 0;

  let config = get_config(),
    discount_codes = Store.state.discount_codes,
    availability = {
      total: 0,
      welfare: false,
      is_known: false,
      details: {
        integration: 0,
        discount_codes: 0,
      },
    };

  if (config == undefined) return availability;
  if (config.integration == undefined) return availability;

  let is_jointly = config.integration.integration === "jointly";

  /**
   * cia: config.integration.availability può arrivare -1 ad indicare che il credito non è conosciuto
   * availability.is_known: per tracciare se la disponibilità è conosciuta oppure no
   */
  let cia = round(number(config.integration.availability), 2);
  let real_availability = cia > 0 ? cia : 0;

  availability.total = real_availability;

  availability.details.integration = real_availability;
  availability.welfare = config.integration.welfare ? true : false;
  availability.is_known = cia > -1 ? true : false;

  if (discount_codes.length) {
    discount_codes.forEach((dc) => {
      let value_resodual = number(dc.valueResidual);
      availability.details.discount_codes = sum(
        availability.details.discount_codes + value_resodual
      );
      availability.total = sum(availability.total + value_resodual);
    });
  }

  if (get_to_points(availability.total)) {
    availability.points = get_to_points(availability.total);
  }

  /**
   * affordable:
   * non viene più ritornato, va calcolato internamente per determinare availability.can_go_to_checkout (vedi sotto)
   */
  let affordable = true;
  if (price > 0 && availability.is_known && !is_jointly) {
    if (smaller(availability.total, price)) affordable = false;
  }

  /*
   * can_go_to_checkout (ex "can_checkout"):
   * - credito (totale) non sufficiente + welfare:false => POSSO andare al checkout ed integrare con carta di credito
   * - credito (totale) non sufficiente + welfare:true => NON POSSO andare al checkout
   */
  availability.can_go_to_checkout =
    is_jointly || !availability.welfare || affordable;

  /*
   * show_credit_alert (ex "affordable"):
   * - sostituisce il vecchio availability.affordable, che ora NON viene ritornato
   * - pilota copy "Credito non sufficiente" a front
   * - RIAGGANCIATO a logiche di prezzo, cioè a .can_go_to_checkout (per coerenza con tasto "Prenota" abilitato o meno)
   */
  availability.show_credit_alert = !availability.can_go_to_checkout;

  return availability;
}

export function get_to_points(amount) {
  /**
   * cgcp: config.guiSettings.customizations.points indica una customizzazione con i punti
   * points_rate: percentuale di conversione in punti
   * points: punti calcolati (arrotondati all'intero più vicino)
   */

  // usa il getter dello store per verificare la disponibilità dei punti
  if (!Store.getters.points_available) return null;
  // silent bug: al primo render in /detail amount era undefined e rompeva heading.vue
  if (amount == undefined) return null;

  let cgcp = Vue.prototype.$config.guiSettings.customizations.points;
  let points_rate = number(cgcp.availabilityPointsRate || 0);
  let points = round(multiply(amount, points_rate));

  return {
    ...cgcp,
    total: points,
  };
}

export function parse_date(date) {
  let split = date.split("T"),
    day_number = split[0].split("-")[2],
    month_number = split[0].split("-")[1],
    year = split[0].split("-")[0],
    new_date = new Date(year, parseInt(month_number) - 1, day_number),
    month_string = new_date.toLocaleString("default", { month: "long" });

  if (split[1] && split[1].length > 5) split[1] = split[1].slice(0, -3); // rimuove i secondi (:00)

  // yyy-mm-dd to dd-mm-yyyy
  function pad(s) {
    return s < 10 ? "0" + s : s;
  }
  var d = new Date(split[0] + " " + split[1]);

  let readable_datetime =
    [pad(d.getDate()), pad(d.getMonth() + 1), d.getFullYear()].join("-") +
    " " +
    split[1];

  return {
    day: split[0],
    hour: split[1],
    day_number: split[0].split("-")[2],
    month_number: split[0].split("-")[1],
    year: split[0].split("-")[0],
    month_string: month_string,
    readable_datetime: readable_datetime,
    friendly_date:
      day_number + " " + month_string + " " + split[0].split("-")[0],
    friendly_date_short:
      day_number + " " + month_string.slice(0, 3).toUpperCase(),
  };
}

export function reverse_date(date) {
  // yyyy-mm-dd to dd-mm-yyyy and viceversa

  //return date.split('-').reverse().join('-');

  if (date == "") return "";

  function pad(s) {
    return s < 10 ? "0" + s : s;
  }

  let dateToUse = date;

  if (date.indexOf("T") == -1) dateToUse = date + "T00:00:00"; // se non c'è l'orario viene applicato il fuso dell'utente

  var d = new Date(dateToUse);

  let reversed = [
    pad(d.getDate()),
    pad(d.getMonth() + 1),
    d.getFullYear(),
  ].join("-");

  return reversed;
}

export function custom_event(action, label, value, category) {
  label = label || "";
  value = value || 0;
  category = category || "";

  if (Vue.prototype.$gtm) {
    let gtmPayload;
    if (typeof action === "object") {
      // custom_event usata per passare direttamente eventi GA4
      gtmPayload = action;
    } else {
      gtmPayload = {
        event: "customEvent",
        category: category ? category : "ce",
        action: action,
        label: label,
        value: value,
      };
    }
    Vue.prototype.$gtm.trackEvent(gtmPayload);
  }

  // Rybbit: delega alla funzione centralizzata con normalizzazione
  rybbit_event_from_custom(action, label, value, category);

  if (typeof action === "object") action = action.event; // nel caso sia stato passato direttamente evento GA4
  if (window.clarity) window.clarity("set", action, label);
  if (window.LogRocket) window.LogRocket.track(action + "_" + label);
}

export function encode_parameters(params) {
  return btoa(encodeURIComponent(JSON.stringify(params)));
}

export function decode_parameters(params) {
  return JSON.parse(decodeURIComponent(atob(params)));
}

export function deep_clone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

export function is_mobile() {
  if (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    )
  ) {
    return true;
  } else {
    return false;
  }
}

export function format_date_YYYY_MM_DD(date = new Date(), separator = "") {
  // nested arrow function
  let pad_to_2_digits = (num) => {
    return num.toString().padStart(2, "0");
  };
  return [
    date.getFullYear(),
    pad_to_2_digits(date.getMonth() + 1),
    pad_to_2_digits(date.getDate()),
  ].join(separator);
}

export function get_traveler_birth_range_dates(departure, pax_age) {
  // formato di ritorno richiesto dal picker: 2024-03-20

  let min = new Date(departure),
    max = new Date(departure);

  if (pax_age >= 30) pax_age = 12; // alpitour adults default 30
  // facendo il range in accordo con il tipo pax
  // si crea un conflitto con i CHD considerati ADT (che ritornatno ADT ed age 30)
  // rendendo impossibile indicare la data di nascita
  // come fix, nel caso ADT, considero 12 anni (che sono sempre CHD):
  //   a noi interessa rendere facile per l'utente mettere le date di nascita di chd e inf, che resta invariato
  //   nel caso di ADT, potrebbe essere possibile inserire date nascita "da minorenni"... pazienza

  min = new Date(min.setFullYear(1900));
  max = new Date(max.setFullYear(max.getFullYear() - pax_age));
  max.setDate(max.getDate() - 2); // evita che il pax compia gli anni nei 2 giorni prima della partenza

  min = format_date_YYYY_MM_DD(min, "-");
  max = format_date_YYYY_MM_DD(max, "-");

  return {
    min: min,
    max: max,
  };
}

export function format_price(price) {
  if (price.length == 1) {
    return price.replace(/\D+/g, "") ? price.replace(/\D+/g, "") : 0;
  }

  let currency = "€",
    separator = "",
    price_split = [];

  if (price.includes(",")) {
    price = price.replace(/[^\d,]/g, "");
    separator = ",";
    price_split = price.split(separator);
  } else if (price.includes(".")) {
    price = price.replace(/[^\d.]/g, "");
    separator = ".";
    price_split = price.split(separator);
  } else {
    price = price.replace(/\D+/g, "");
  }

  if (price_split.length > 1) {
    price =
      price_split[0] +
      separator +
      price_split[1].replace(/\D+/g, "").slice(0, 2);
  }

  if (price.length) return currency + " " + price;

  return "";
}

export function get_type_icon(type = "") {
  switch (type) {
    case "hotel":
    case "property":
      return "hotel";

    case "city":
      return "city";

    case "district":
    case "subdistrict":
      return "district";

    case "region":
      return "region";

    case "country":
      return "country";

    case "airport":
      return "airplane";

    case "landmark":
      return "landmark";

    case "map":
      return "map";

    case "point":
      return "map";

    default:
      return "pin";
  }
}

export function get_site_name() {
  return this.get_config().site.brand + " " + this.get_config().site.payoff;
}

// Map with components allowed to be inserted between result pads
const ALLOWED_EXTRA_COMPONENTS = {
  "marketing-pad": true,
  img: true,
};

// Map with condition key and the related check
const COMPONENTS_CONDITIONS = {
  hasWelfare: (v) => get_config("integration.welfare") === v,
  // Loyalty -> customization loyalty + welfare:false
  hasLoyalty: (v) => {
    if (get_config("integration.welfare")) {
      return !v;
    }
    const points = get_config("guiSettings.customizations.points", {});
    return Object.keys(points).length > 0 === v;
  },
  // Convenzioni -> customization convenzioni + welfare:false
  hasConventions: (v) => {
    if (get_config("integration.welfare")) {
      return !v;
    }
    const loyalty = [
      "showValueAdds",
      "showDeal",
      "strikethrough",
      "sennaFilters",
    ].every((key) => {
      const value = get_config(`guiSettings.customizations.${key}`);
      if (!value) {
        return false;
      }
      if (typeof value === "boolean") {
        return value;
      }
      return Object.keys(value).length > 0;
    });
    return loyalty === v;
  },
};

export const add_extra_components_to_results = (
  results = [],
  id_field = "id"
) => {
  try {
    // Sanity check
    if (!results || results.length === 0) {
      return results;
    }

    const {
      items: config_items = [],
      positions: config_positions = [],
      repeat = false,
    } = get_config("guiSettings.customizations.resultsExtraComponents", {});

    // Sanity check: informations must be correct to render
    if (config_items.length === 0 || config_positions.length === 0) {
      return results;
    }

    // Filter invalid positions
    const positions = config_positions
      .filter((p) => !isNaN(p))
      .map(Number)
      .filter((p) => p > 0);
    if (positions.length === 0) {
      return results;
    }

    // Filter invalid components
    const items = config_items
      .filter((item) => Object.keys(item || {}).length > 0)
      .filter((item) => ALLOWED_EXTRA_COMPONENTS[String(item.component)])
      .filter((item) => {
        if (!item.conditions) {
          return true;
        }
        return Object.entries(item.conditions).every((entry) => {
          const [conditions, params = []] = entry;
          return conditions.split("|").some((condition) => {
            const validator = COMPONENTS_CONDITIONS[condition];
            return !validator || validator(...params);
          });
        });
      });

    if (items.length === 0) {
      return results;
    }
    const injected = [...results];

    let result_relative_index = 0;
    let item_index = 0;
    let position_index = 0;
    for (
      let result_index = 0;
      result_index < results.length;
      result_index += 1
    ) {
      // Insert after every N items (but not after the last item)
      if (
        result_relative_index === positions[position_index] &&
        result_index !== results.length - 1
      ) {
        injected.splice(result_index, 0, {
          [id_field]: `result-extra:${result_index}`,
          ...items[item_index],
        });
        item_index += 1;
        position_index += 1;
        result_relative_index = 0;
      } else {
        result_relative_index += 1;
      }

      if (position_index >= positions.length) {
        position_index = 0;
      }

      if (item_index >= items.length) {
        if (!repeat) {
          return injected;
        }
        item_index = 0;
      }
    }

    return injected;
  } catch (e) {
    // If anything goes wrong fallbackto normal resutls with no injected components
    this.log("ERROR: add_extra_components_to_results", e);
    return results;
  }
};
